import type { CorrelationMeasureId } from './correlation-measures.model';
import { CORRELATION_MEASURE_DEFS } from './correlation-measures.model';
import type { CorrelationIntermediates, CorrelationMeasureValues } from './correlation-measures.util';

// =============================================================================
// Public API
// =============================================================================

/**
 * Build a single aligned LaTeX block that combines the symbolic formula with the
 * plugged-in calculation. Each step is on its own line, aligned on the `=` sign.
 *
 * Example output for slope:
 *   a &= \dfrac{n\sum x_i y_i - ...}{...}
 *     &= \dfrac{20 \cdot 17652 - ...}{...}
 *     &= \dfrac{31065}{17131}
 *     &= \underline{\mathbf{1.8134}}
 */
export function buildCombinedCorrelationFormula(
  id: CorrelationMeasureId,
  inter: CorrelationIntermediates,
  measures: CorrelationMeasureValues,
): string {
  const lines = buildAlignedLines(id, inter, measures);
  if (!lines.length) {
    return '';
  }
  return `$$\\begin{aligned} ${lines.join(' \\\\[6pt] ')} \\end{aligned}$$`;
}

/**
 * Build a single aligned block for the full regression equation derivation:
 * slope (a) and intercept (b) together, so that a and b align on the left.
 */
export function buildEquationDerivationFormula(
  inter: CorrelationIntermediates,
  measures: CorrelationMeasureValues,
): string {
  const slopeLines = buildAlignedLines('slope', inter, measures);
  const interceptLines = buildAlignedLines('intercept', inter, measures);
  if (!slopeLines.length && !interceptLines.length) {
    return '';
  }

  // Use larger gap between the slope and intercept groups
  return `$$\\begin{aligned} ${slopeLines.join(' \\\\[6pt] ')} \\\\[16pt] ${interceptLines.join(
    ' \\\\[6pt] ',
  )} \\end{aligned}$$`;
}

/**
 * Build a LaTeX string showing the formula with actual data values plugged in.
 * The final result value is highlighted with bold + underline.
 */
export function buildCorrelationPluggedInFormula(
  id: CorrelationMeasureId,
  inter: CorrelationIntermediates,
  measures: CorrelationMeasureValues,
): string {
  const { n, sumX, sumY, sumXY, sumX2, sumY2, meanX, meanY, xValues, yValues } = inter;

  const fmt = (v: number) => formatVal(v);
  const hi = (v: number) => highlight(v);

  switch (id) {
    case 'n':
      return `$n = ${hi(n)}$`;

    case 'meanX':
      return `$\\bar{x} = \\dfrac{${ellipsisSumList(xValues)}}{${n}} = ${hi(meanX)}$`;

    case 'meanY':
      return `$\\bar{y} = \\dfrac{${ellipsisSumList(yValues)}}{${n}} = ${hi(meanY)}$`;

    case 'stdX': {
      const stdX = asNumber(measures.get('stdX'));
      return `$S_x = \\sqrt{\\dfrac{${ellipsisDeviations(xValues, meanX)}}{${n}}} = ${hi(stdX)}$`;
    }

    case 'stdY': {
      const stdY = asNumber(measures.get('stdY'));
      return `$S_y = \\sqrt{\\dfrac{${ellipsisDeviations(yValues, meanY)}}{${n}}} = ${hi(stdY)}$`;
    }

    case 'r': {
      const r = asNumber(measures.get('r'));
      const numerator = n * sumXY - sumX * sumY;
      const denomX = n * sumX2 - sumX * sumX;
      const denomY = n * sumY2 - sumY * sumY;
      return `$r = \\dfrac{${fmt(n)} \\cdot ${fmt(sumXY)} - ${fmt(sumX)} \\cdot ${fmt(sumY)}}{\\sqrt{(${fmt(
        n,
      )} \\cdot ${fmt(sumX2)} - ${fmt(sumX)}^2)(${fmt(n)} \\cdot ${fmt(sumY2)} - ${fmt(sumY)}^2)}} = \\dfrac{${fmt(
        numerator,
      )}}{\\sqrt{${fmt(denomX)} \\cdot ${fmt(denomY)}}} = ${hi(r)}$`;
    }

    case 'rSquared': {
      const r = asNumber(measures.get('r'));
      const rSq = asNumber(measures.get('rSquared'));
      return `$r^2 = (${fmt(r)})^2 = ${hi(rSq)}$`;
    }

    case 'slope': {
      const a = asNumber(measures.get('slope'));
      const numerator = n * sumXY - sumX * sumY;
      const denominator = n * sumX2 - sumX * sumX;
      return `$a = \\dfrac{${fmt(n)} \\cdot ${fmt(sumXY)} - ${fmt(sumX)} \\cdot ${fmt(sumY)}}{${fmt(n)} \\cdot ${fmt(
        sumX2,
      )} - ${fmt(sumX)}^2} = \\dfrac{${fmt(numerator)}}{${fmt(denominator)}} = ${hi(a)}$`;
    }

    case 'intercept': {
      const a = asNumber(measures.get('slope'));
      const b = asNumber(measures.get('intercept'));
      return `$b = ${fmt(meanY)} - ${fmt(a)} \\cdot ${fmt(meanX)} = ${hi(b)}$`;
    }

    case 'equation': {
      const a = asNumber(measures.get('slope'));
      const b = asNumber(measures.get('intercept'));
      if (b >= 0) {
        return `$\\hat{y} = ${hi(a)}x + ${hi(b)}$`;
      }
      return `$\\hat{y} = ${hi(a)}x - ${hi(Math.abs(b))}$`;
    }
  }
}

// =============================================================================
// Helpers
// =============================================================================

function highlight(v: number): string {
  return `\\underline{\\mathbf{${formatVal(v)}}}`;
}

function asNumber(value: number | string | undefined): number {
  if (typeof value === 'number') {
    return value;
  }
  return 0;
}

function formatVal(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toFixed(4)).toString();
}

/** Comma-separated list with ellipsis for large arrays. */
function ellipsisSumList(data: readonly number[]): string {
  if (data.length <= 6) {
    return data.map((v) => formatVal(v)).join(' + ');
  }
  const first = data.slice(0, 3).map((v) => formatVal(v));
  const last = formatVal(data[data.length - 1]);
  return `${first.join(' + ')} + \\cdots + ${last}`;
}

/** Deviation squared terms with ellipsis. */
function ellipsisDeviations(data: readonly number[], mean: number): string {
  const mu = formatVal(mean);
  if (data.length <= 4) {
    return data.map((v) => `(${formatVal(v)} - ${mu})^2`).join(' + ');
  }
  const first = data.slice(0, 2).map((v) => `(${formatVal(v)} - ${mu})^2`);
  const last = `(${formatVal(data[data.length - 1])} - ${mu})^2`;
  return `${first.join(' + ')} + \\cdots + ${last}`;
}

/**
 * Build the aligned lines (without the wrapper) for a single measure.
 * Returns array like: ["a &= \\dfrac{...}{...}", "&= \\dfrac{20 ...}{...}", "&= result"]
 */
function buildAlignedLines(
  id: CorrelationMeasureId,
  inter: CorrelationIntermediates,
  measures: CorrelationMeasureValues,
): string[] {
  const def = CORRELATION_MEASURE_DEFS.find((d) => d.id === id);
  if (!def) {
    return [];
  }

  const pluggedIn = buildCorrelationPluggedInFormula(id, inter, measures);

  const general = def.formulaLatex.replace(/^\$+|\$+$/g, '').trim();
  const pluggedInStr = pluggedIn.replace(/^\$+|\$+$/g, '').trim();

  const gEqIdx = general.indexOf('=');
  const pEqIdx = pluggedInStr.indexOf('=');

  if (gEqIdx === -1 || pEqIdx === -1) {
    return [];
  }

  const lhs = general.substring(0, gEqIdx).trim();
  const generalRhs = general.substring(gEqIdx + 1).trim();
  const pluggedInRhs = pluggedInStr.substring(pEqIdx + 1).trim();

  const steps = splitOnTopLevelEquals(pluggedInRhs);

  const lines = [`${lhs} &= ${generalRhs}`];
  for (const step of steps) {
    lines.push(`&= ${step.trim()}`);
  }

  return lines;
}

/** Split a LaTeX string on `=` signs that are not inside `{...}` braces. */
function splitOnTopLevelEquals(latex: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';

  for (const ch of latex) {
    if (ch === '{') {
      depth++;
    } else if (ch === '}') {
      depth--;
    } else if (ch === '=' && depth === 0) {
      if (current.trim()) {
        parts.push(current);
      }
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) {
    parts.push(current);
  }

  return parts;
}
